import { useNostr } from "@nostrify/react";
import { useQuery } from "@tanstack/react-query";

import { useCurrentUser } from "./useCurrentUser";
import {
  ADMIN_PUBKEY,
  ORDER_KIND,
  ORDER_RELAYS,
  type OrderPayload,
} from "@/lib/cashcraft";

import type { NostrEvent } from "@nostrify/nostrify";

export interface DecryptedOrder {
  event: NostrEvent;
  payload: OrderPayload | null;
  /** True if decryption failed (e.g. not the operator, or malformed). */
  error: boolean;
}

/**
 * Fetches website build orders addressed to the operator and decrypts them.
 * Only works when the logged-in user is the operator (they hold the key that
 * can decrypt the NIP-44 content).
 */
export function useOrders() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  const isAdmin = user?.pubkey === ADMIN_PUBKEY;

  return useQuery<DecryptedOrder[]>({
    queryKey: ["orders", user?.pubkey ?? "anon"],
    enabled: isAdmin,
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);
      const filter = {
        kinds: [ORDER_KIND],
        "#p": [ADMIN_PUBKEY],
        limit: 200,
      };

      // Read from the fixed order relays and the default pool, then dedupe.
      const results = await Promise.allSettled([
        nostr.group(ORDER_RELAYS).query([filter], { signal }),
        nostr.query([filter], { signal }),
      ]);

      const byId = new Map<string, NostrEvent>();
      for (const r of results) {
        if (r.status === "fulfilled") {
          for (const event of r.value) byId.set(event.id, event);
        }
      }

      const sorted = [...byId.values()].sort(
        (a, b) => b.created_at - a.created_at,
      );

      const decrypted = await Promise.all(
        sorted.map(async (event): Promise<DecryptedOrder> => {
          try {
            if (!user?.signer.nip44) {
              return { event, payload: null, error: true };
            }
            const plaintext = await user.signer.nip44.decrypt(
              event.pubkey,
              event.content,
            );
            const payload = JSON.parse(plaintext) as OrderPayload;
            return { event, payload, error: false };
          } catch {
            return { event, payload: null, error: true };
          }
        }),
      );

      return decrypted;
    },
  });
}
